import * as React from 'react'
import { TextField } from '../ui/TextField'
import { Button, ButtonText } from '../ui/button'
import { useController, useForm, useWatch } from 'react-hook-form'
import { DateAfter } from 'react-day-picker'
import { unformatNumeral } from 'cleave-zen'
import AmountInput from '@/components/AmountInput'
import NonModalWindow from '@/components/NonModalWindow'
import useEditVault from '@/services/api/vault/useEditVault'
import { format } from 'date-fns'
import { useParams } from 'react-router-dom'
import useCloseToOrigin from '@/hooks/useCloseToOrigin'
import useGetVault from '@/services/api/cache/useGetVault'
import ConfirmationModal from '../ConfirmationModal'
import useGetPrearchiveVault from '@/services/api/vault/useGetPrearchiveVault'
import { Skeleton } from '../ui/skeleton'

export interface VaultFormValues {
  name: string
  description: string
  opening_balance: {
    amount: number
    transaction_date: string
  }
}

export default function EditVaultForm() {
  const closeToOrigin = useCloseToOrigin()
  const { vaultId } = useParams()

  const { data: editedEntity, refetch } = useGetVault({ vaultId })
  const { data: prearchive, status: prearchiveStatus } =
    useGetPrearchiveVault(vaultId)
  const hasTransactions = prearchive?.requires_transfer ?? true

  const [isFormDirty, setIsFormDirty] = React.useState(false)
  const [openConfirmation, setOpenConfirmation] = React.useState(false)
  const [isFocusedName, setIsFocusedName] = React.useState(false)
  const [isFocusedDescription, setIsFocusedDescription] = React.useState(false)
  const [forceEdit, setForceEdit] = React.useState(false)

  const editable = forceEdit || !hasTransactions

  const {
    register,
    handleSubmit,
    formState: { errors, defaultValues },
    control,
    setValue,
  } = useForm<VaultFormValues>({
    defaultValues: async () => {
      const { data: editedEntity } = await refetch()
      return {
        name: editedEntity.name || '',
        description: editedEntity.description || '',
        opening_balance: {
          amount: editedEntity.opening_balance?.amount || 0,
          transaction_date:
            editedEntity.opening_balance?.transaction_date || '',
        },
      }
    },
  })

  const name = useWatch({
    control,
    name: 'name',
  })

  const description = useWatch({
    control,
    name: 'description',
  })

  const [amount, setAmount] = React.useState(
    editedEntity?.opening_balance.amount,
  )
  const [date, setDate] = React.useState(
    editedEntity?.opening_balance.transaction_date,
  )

  React.useEffect(() => {
    setValue('opening_balance.amount', amount)
  }, [amount])

  React.useEffect(() => {
    setValue('opening_balance.transaction_date', date)
  }, [date])

  const handleAmountChange = (event) => {
    const value = event.target.value
    if (value.length <= 13) {
      setAmount(value)
    }
  }

  const handleNameChange = (event) => {
    const value = event.target.value
    if (value.length <= 30) {
      setValue('name', value)
    }
  }

  const handleDescriptionChange = (event) => {
    const value = event.target.value
    if (value.length <= 40) {
      setValue('description', value)
    }
  }

  const { field: dateField } = useController({
    control: control,
    name: 'opening_balance.transaction_date',
    shouldUnregister: true,
  })

  const { field: amountField } = useController({
    control: control,
    name: 'opening_balance.amount',
    shouldUnregister: true,
  })

  const { editVault } = useEditVault(vaultId)

  const watchedValues = useWatch({ control })

  React.useEffect(() => {
    const isDirty =
      watchedValues.name !== defaultValues?.name ||
      watchedValues.description !== defaultValues?.description ||
      watchedValues.opening_balance?.amount !==
        defaultValues?.opening_balance?.amount

    setIsFormDirty(isDirty)
  }, [watchedValues, defaultValues])

  const handleClose = () => {
    if (isFormDirty) {
      setOpenConfirmation(true)
    } else {
      closeToOrigin()
    }
  }

  const onSubmit = async (data) => {
    const { opening_balance } = data
    const amountNum = parseFloat(
      unformatNumeral(opening_balance.amount.toString()),
    )
    await editVault({
      ...data,
      amount: amountNum,
      transaction_date: opening_balance.transaction_date,
    })
    closeToOrigin()
  }

  const afterMatcher: DateAfter = { after: new Date() }

  if (!defaultValues || !editedEntity || prearchiveStatus === 'pending')
    return (
      <div className="p-4">
        <div className="mt-2 max-h-[50vh] overflow-hidden">
          <Skeleton className="mb-2 h-12 w-full" />

          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((x) => (
            <div key={x}>
              <Skeleton className="h-3 w-[40%]" />
              <Skeleton className="mt-2 h-6" />
              <Skeleton className="my-4 h-0.5" />
            </div>
          ))}
        </div>
      </div>
    )

  return (
    <>
      <ConfirmationModal
        type="Edit Vault"
        open={openConfirmation}
        setOpen={setOpenConfirmation}
      />
      <form
        className=" flex flex-col"
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit(onSubmit)
        }}
      >
        <NonModalWindow
          title="Edit Vault"
          onClose={() => handleClose()}
          isDirty={isFormDirty}
        >
          <NonModalWindow.Body className="min-h-[78vh]">
            <p className="mb-3 text-sm text-txt-secondary">General</p>
            <div className="gap-4- flex flex-col gap-field-gap">
              <div className="mb-3">
                <TextField
                  status={errors?.name ? 'error' : null}
                  statusText={errors.name?.message}
                >
                  <TextField.Label className="flex  justify-between text-sm">
                    Vault Name
                    <span
                      className={`text-txt-secondary ${isFocusedName ? '' : 'hidden'}`}
                    >
                      {name.length}/30
                    </span>
                  </TextField.Label>
                  <TextField.Input
                    maxLength={30}
                    placeholder="e.g., Bank Name"
                    {...register('name', {
                      required: 'Please fill out this field',
                      value: name,
                      onChange: handleNameChange,
                    })}
                    onFocus={() => setIsFocusedName(true)}
                    onBlur={() => setIsFocusedName(false)}
                  />
                </TextField>
              </div>
              <div>
                <TextField>
                  <TextField.Label className="flex  justify-between text-sm">
                    Description
                    <span
                      className={`text-txt-secondary ${isFocusedDescription ? '' : 'hidden'}`}
                    >
                      {description ? description.length : '0'}/40
                    </span>
                  </TextField.Label>
                  <TextField.Input
                    maxLength={40}
                    placeholder="e.g., Main Account"
                    {...register('description', {
                      value: description,
                      onChange: handleDescriptionChange,
                    })}
                    onFocus={() => setIsFocusedDescription(true)}
                    onBlur={() => setIsFocusedDescription(false)}
                  />
                </TextField>
              </div>

              <p className="text-txt-secondary">Vault Opening Balance</p>
              <div className="mb-3">
                <TextField
                  status={errors?.opening_balance ? 'error' : null}
                  statusText={errors.opening_balance?.message}
                >
                  <TextField.Label className="text-sm">Amount</TextField.Label>
                  <AmountInput
                    placeholder="0.00"
                    disabled={!editable}
                    controllerField={amountField}
                    onChange={handleAmountChange}
                  />
                </TextField>
              </div>
              <div className="mb-1">
                <TextField>
                  <TextField.Label className="text-sm">
                    Date as of
                  </TextField.Label>
                  <TextField.DatePicker
                    value={
                      dateField.value
                        ? format(dateField.value, 'yyyy-MM-dd')
                        : ''
                    }
                    onSelect={dateField.onChange}
                    disabled={afterMatcher}
                    inputDisabled={!editable}
                    {...register('opening_balance.transaction_date')}
                  />
                </TextField>
              </div>
              {!editable && (
                <div className="mb-4">
                  <p className="mb-3 text-txt-disabled">
                    These fields are disabled because there are existing
                    transactions in this Vault. Changing these fields will
                    trigger a recalculation, which may result in balance
                    discrepancies. We do not recommend modifying these fields
                  </p>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setForceEdit(true)}
                  >
                    I still want to change these fields
                  </Button>
                </div>
              )}
              {editable && editedEntity?.opening_balance?.amount !== 0 && (
                <p className="text-txt-note">
                  Changing these fields will trigger a recalculation, which may
                  result in balance discrepancies. We do not recommend modifying
                  these fields
                </p>
              )}
            </div>
          </NonModalWindow.Body>
          <NonModalWindow.Footer>
            <Button
              variant="secondary"
              className="mr-2"
              onClick={() => handleClose()}
            >
              <ButtonText>Cancel</ButtonText>
            </Button>
            <Button type="submit" onClick={handleSubmit(onSubmit)}>
              <ButtonText>Save</ButtonText>
            </Button>
          </NonModalWindow.Footer>
        </NonModalWindow>
      </form>
    </>
  )
}
